import React from 'react';
import type { ResumeData } from '../types';
import { formatDate } from '../utils';

export function ModernTemplate({ data }: { data: ResumeData }) {
  return (
    <div className="max-w-4xl mx-auto bg-white p-8 shadow-lg">
      {/* Header */}
      <header className="text-center mb-8 pb-8 border-b">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{data.personalInfo.fullName}</h1>
        <div className="text-gray-600 space-y-1">
          <p>{data.personalInfo.email} • {data.personalInfo.phone}</p>
          <p>{data.personalInfo.location}</p>
          {data.personalInfo.website && <p>{data.personalInfo.website}</p>}
        </div>
      </header>

      {/* Summary */}
      {data.personalInfo.summary && (
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-3 text-gray-800">Professional Summary</h2>
          <p className="text-gray-700">{data.personalInfo.summary}</p>
        </section>
      )}

      {/* Experience */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Experience</h2>
        {data.experiences.map((exp) => (
          <div key={exp.id} className="mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">{exp.position}</h3>
                <h4 className="text-lg text-gray-600">{exp.company}</h4>
              </div>
              <div className="text-gray-600">
                {formatDate(exp.startDate)} - {exp.isCurrentJob ? 'Present' : formatDate(exp.endDate)}
              </div>
            </div>
            <p className="mt-2 text-gray-700">{exp.description}</p>
            {exp.achievements.length > 0 && (
              <ul className="mt-2 list-disc list-inside text-gray-700">
                {exp.achievements.map((achievement, index) => (
                  <li key={index}>{achievement}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </section>

      {/* Education */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Education</h2>
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">{edu.degree}</h3>
                <h4 className="text-lg text-gray-600">{edu.institution}</h4>
              </div>
              <div className="text-gray-600">
                {formatDate(edu.startDate)} - {edu.isPursuing ? 'Present' : formatDate(edu.endDate)}
              </div>
            </div>
            {edu.grade && <p className="text-gray-700">Grade: {edu.grade}</p>}
          </div>
        ))}
      </section>

      {/* Skills */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Skills</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-lg font-medium mb-2 text-gray-700">Technical Skills</h3>
            <p className="text-gray-600">{data.skills.technical}</p>
          </div>
          <div>
            <h3 className="text-lg font-medium mb-2 text-gray-700">Soft Skills</h3>
            <p className="text-gray-600">{data.skills.soft}</p>
          </div>
        </div>
      </section>
    </div>
  );
}